import { defaultParseSearch, defaultStringifySearch } from "@tanstack/react-router";

/**
 * Params whose value is free text a person typed, never JSON.
 *
 * TanStack gives every search param JSON semantics, which is what makes
 * `?page=3` a number and `?filterEI=true` a boolean. For a *typed* value that
 * is wrong, and silently so: JSON reads `?search=1.2300` back as `1.23`,
 * `?search={}` as an object, and a digit run longer than a JS number holds as a
 * rounded float. The user is shown results for something they did not type.
 *
 * The donor has no such layer — `items-list-page.tsx` writes and reads its term
 * with plain `URLSearchParams` (`searchParams.set('search', search)`,
 * `searchParams.get('search')`), so in modsy `?search=1.2300` *is* the string
 * `1.2300`. Keeping that is parity, not a nicety: a Spec Tool link carrying a
 * JSON-shaped term has to keep meaning what it meant.
 *
 * Free text is the exception, not the rule — everything else in the app's URL
 * vocabulary is a number, a boolean, an enum or a list, and those want JSON.
 */
const LITERAL_TEXT_PARAMS = new Set([
  "search",
  // The details page's line search (#695) — `searchParams.get('specSearch')`
  // in the donor (`shared/data/loader.ts`), the same plain text.
  "specSearch",
  // The edit page's Item Search island term (#811) —
  // `searchParams.get('itemSearch')` in the donor (`SearchInput.tsx`), the
  // same plain text beside the editor's own `specSearch`.
  "itemSearch",
  // The details page's deep link (#695) — `searchParams.get('lineId')` and
  // `get('selectionId')`, raw text the donor `parseInt`s where it uses them
  // and never writes back, so `0x1A` stays `0x1A` in the URL.
  "lineId",
  "selectionId",
  // The Spec list's column filters (#692) — `searchParams.get('filters')`, the
  // JSON *text* the donor's own parser reads, written back with
  // `params.set('filters', JSON.stringify(…))`. Read as JSON here it would
  // arrive as a list, and the route's string schema would drop it.
  "filters",
  // The Admin Integrations tab's direction (#747) — `searchParams.get('mode')`
  // in the donor (`ExternalApis.tsx:561-563`), read and never written back. As
  // JSON, `?mode=1` or `?mode=true` would arrive as a number or a boolean, the
  // admin schema would drop it, and the router would rewrite it out of the
  // address bar the donor left alone. The key is app-wide, like every entry
  // here: a later route that takes a `?mode=` reads it as text too, and
  // verbatim (below) — check that is what that route's donor did.
  "mode",
  // A public share page's token (#1022) — `URLSearchParams.get('share_token')`
  // in the donor (`route-utils.ts:52-56`). A credential, never JSON: a garbled
  // `?share_token=1e5` must reach the server as the text it is. Written back
  // unencoded when it is a real token (`UNENCODED_PARAMS`, below).
  "share_token",
]);

/**
 * Literal-text params kept exactly as the link spelled them — an empty value, a
 * bare key, a repeat — because the donor only ever *read* them. Its tab switch
 * rebuilt the query from `new URLSearchParams(prev)`, which carries every
 * spelling through untouched; a param the app writes itself (`search`) instead
 * spells "nothing" as absence, which is why this is not every literal param.
 *
 * A repeat reads as the list of its values, in order.
 */
const VERBATIM_PARAMS = new Set(["mode"]);

/**
 * Literal-text params the donor writes into its links **unencoded** — the share
 * token, interpolated raw into every public URL it builds (`navigation.ts:
 * 52-54`; `public-spec-latest-redirect.tsx:111`). The router re-serialises every
 * location it enters, so without this the address bar would turn a handed-out
 * `share_token=gAAAA…==` into `…%3D%3D`: the same token, a different string from
 * the one in the visitor's email. Written raw only while the value holds nothing
 * but a Fernet token's own alphabet — URL-safe base64 and its `=` padding,
 * none of which a query string misreads; anything else is encoded as usual.
 */
const UNENCODED_PARAMS = new Set(["share_token"]);
const UNENCODED_SAFE = /^[A-Za-z0-9_=-]+$/;

/**
 * The router's URL-search parser: TanStack's, with the literal-text params read
 * straight off the query string instead of through JSON.
 */
export function parseAppSearch(searchStr: string): Record<string, unknown> {
  const parsed = defaultParseSearch(searchStr) as Record<string, unknown>;
  const raw = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);

  for (const key of LITERAL_TEXT_PARAMS) {
    // `URLSearchParams` has already percent-decoded, and decodes `+` as a
    // space, so both spellings of a spaced term arrive the same way.
    const values = raw.getAll(key);
    if (values.length === 0) continue;
    parsed[key] = VERBATIM_PARAMS.has(key) && values.length > 1 ? values : values[0];
  }
  return parsed;
}

/**
 * One literal-text param, encoded by the serializer the donor itself uses.
 *
 * `URLSearchParams.toString()` *is* `application/x-www-form-urlencoded`, which
 * is where the donor's term ends up: `setSearchParams` finishes as
 * `navigate("?" + newSearchParams)`. Handing the job to the same class rather
 * than approximating it is the point — `encodeURIComponent` with the spaces
 * swapped for `+` gets the space right and then disagrees about exactly five
 * printable characters, leaving `! ' ( ) ~` raw where the donor escapes them.
 * That is ordinary typing, not a hand-edited URL: `O'Brien` wrote
 * `?search=O'Brien` here against modsy's `?search=O%27Brien`.
 */
function encodeLiteral(key: string, value: string): string {
  if (UNENCODED_PARAMS.has(key) && UNENCODED_SAFE.test(value)) return `${key}=${value}`;
  return new URLSearchParams([[key, value]]).toString();
}

/**
 * The matching writer. The two halves have to agree exactly: a term written as
 * JSON and read as text would come back wearing quotes.
 *
 * **Key order is the param order, literal params included.** The donor builds
 * its whole query string through one `URLSearchParams`, so every param — the
 * term among them — sits where it was first inserted and later writes append
 * after it. Lifting the term out and appending it last produced a different
 * *string* for the same clicks: typing a term and then using a filter wrote
 * `?page=1&filterEI=true&search=…` where modsy writes
 * `?search=…&page=1&filterEI=true`. Same state, different link — and a link
 * copied from either stack being the same string is the whole point of #513.
 *
 * So each key is encoded in place and joined in the object's own order, rather
 * than the structured params being stringified as a block with the literals
 * appended.
 */
export function stringifyAppSearch(search: object): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(search)) {
    if (LITERAL_TEXT_PARAMS.has(key) && typeof value === "string") {
      // Skipped rather than written empty: a key with no value is a param the
      // reader has to special-case, and the app spells "no term" as absence —
      // except where the link's own spelling is the thing being kept.
      if (value !== "" || VERBATIM_PARAMS.has(key)) parts.push(encodeLiteral(key, value));
      continue;
    }
    if (
      VERBATIM_PARAMS.has(key) &&
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      for (const item of value) parts.push(encodeLiteral(key, item));
      continue;
    }
    // One key at a time, through TanStack's own writer, so every structured
    // param keeps exactly the encoding and the `undefined`-dropping it had when
    // the whole object went through at once.
    const encoded = defaultStringifySearch({ [key]: value });
    if (encoded !== "" && encoded !== "?") parts.push(encoded.slice(1));
  }

  return parts.length === 0 ? "" : `?${parts.join("&")}`;
}
